/**
 * The Desktop's Trash window: the workspace's trashed files, as Browse's
 * Trash shows them (the same query, so the two can't disagree), with
 * Restore and Delete permanently from the shared file actions and an
 * "Empty Trash". A list rather than an arranged canvas — trashed files are
 * transient and have no folder to arrange them in — and nothing drags out:
 * Restore puts a file back where it was.
 */
import type { VaultDocumentDto } from '@/lib/api';
import { useVaultDocumentsList } from '../../hooks/useVaultDocuments';
import type { FileActionsController } from '../../hooks/useFileActions';
import { DocumentsView } from '../shared/DocumentsView';

export function TrashWindowContent({
  holderId,
  fileActions,
  canWrite,
  onOpenDocument,
  onEmptyTrash,
}: {
  holderId: string;
  fileActions: FileActionsController;
  canWrite: boolean;
  onOpenDocument: (doc: VaultDocumentDto) => void;
  onEmptyTrash: (count: number) => void;
}) {
  const list = useVaultDocumentsList(holderId, { deletedOnly: true, includeArchived: true });
  const documents = list.data ?? [];
  return (
    <div className="flex h-full flex-col gap-2 overflow-y-auto p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">
          {documents.length > 0
            ? 'Items here are permanently deleted after 30 days.'
            : 'Nothing in the Trash.'}
        </p>
        {canWrite && documents.length > 0 && (
          <button
            type="button"
            onClick={() => onEmptyTrash(list.total)}
            className="rounded border border-destructive/40 bg-card px-3 py-1 text-xs text-destructive hover:bg-destructive/10"
          >
            Empty Trash
          </button>
        )}
      </div>
      <DocumentsView
        mode="list"
        loading={list.isLoading}
        documents={documents}
        folders={[]}
        currentFolderId={null}
        isTrash
        onOpenDocument={onOpenDocument}
        onOpenFolder={() => {}}
        actionsFor={fileActions.actionsFor}
      />
      {list.hasNextPage && (
        <div className="flex items-center justify-center gap-3 text-xs text-muted-foreground">
          <span>
            Showing {documents.length} of {list.total}
          </span>
          <button
            type="button"
            className="rounded border border-border px-3 py-1 text-foreground hover:bg-accent disabled:opacity-50"
            disabled={list.isFetchingNextPage}
            onClick={() => void list.fetchNextPage()}
          >
            {list.isFetchingNextPage ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
}
